import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Slot, usePathname } from 'expo-router';
import Logo from '@/components/Icons/Logo';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme, Themes } from '@/constants/Colors';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { LearnProgressProvider } from './LearnProgress';
import './learn.css';

const Search = React.lazy(() => import('./LearnSearchDialog'));
export default function LearnLayout() {
  const pathname = usePathname();
  const { theme, colors } = useTheme();
  const [appearance, setAppearance] = useState(null);
  const activeTheme = appearance === null ? theme : appearance === 'dark' ? 'black' : 'white';
  const activeColors = appearance === null ? colors : Themes[activeTheme];
  const palette = getCatalogPalette(activeTheme);
  const dark = isDarkTheme(activeTheme);
  const [searchOpen, setSearchOpen] = useState(false);
  const scrollRef = useRef(null);
  const searchRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    const shortcut = event => { if ((event.metaKey || event.ctrlKey) && event.key === 'k') { event.preventDefault(); setSearchOpen(true); } };
    document.addEventListener('keydown', shortcut);
    return () => document.removeEventListener('keydown', shortcut);
  }, []);
  return <LearnProgressProvider>
    <div className="learn-app" data-theme={dark ? 'dark' : 'light'} style={{
      '--learn-bg': activeColors.solid_color_primary,
      '--learn-ink': activeColors.text_color_primary,
      '--learn-muted': activeColors.text_color_secondary,
      '--learn-surface': palette.preview,
      '--learn-border': palette.border,
      '--learn-control': palette.activeBorder,
      '--learn-card': palette.surface,
      '--learn-card-hover': palette.hover,
      '--learn-card-border': palette.border,
    }}>
      <header className="learn-nav"><div className="learn-nav-inner">
        <div className="learn-brand"><Link href="/" aria-label="Gab AI home"><Logo width={24} height={24} color={activeColors.text_color_primary} /></Link><span aria-hidden="true" className="learn-nav-divider" /><Link href="/learn">Learn</Link></div>
        <nav aria-label="Learn navigation">
          <button ref={searchRef} onClick={() => setSearchOpen(true)} className="learn-nav-search"><span aria-hidden="true">⌕</span> Search lessons</button>
          <Link href="/docs" className="learn-nav-docs">Docs</Link>
          <button className="learn-appearance" aria-label={dark ? 'Use light appearance' : 'Use dark appearance'} onClick={() => setAppearance(dark ? 'light' : 'dark')}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">{dark ? <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></> : <path d="M20.5 14A9 9 0 0 1 10 3.5 9 9 0 1 0 20.5 14Z" />}</svg></button>
          <Link href="/" className="learn-button learn-button-small">Open Gab AI <span aria-hidden="true">↗</span></Link>
        </nav>
      </div></header>
      <div className="learn-scroll" ref={scrollRef}>
        <main id="main-content" tabIndex={-1} className="learn-main"><Slot /></main>
        <footer className="learn-footer"><Link href="/learn">Gab AI Learn</Link><span>Learn something. Make something.</span><Link href="/docs">Need product help? Visit Docs ↗</Link></footer>
      </div>
      {searchOpen && <Suspense fallback={<div className="learn-search-loading" role="status">Opening lesson search… <button onClick={() => setSearchOpen(false)}>Cancel</button></div>}><Search isOpen onClose={() => { setSearchOpen(false); searchRef.current?.focus(); }} /></Suspense>}
    </div>
  </LearnProgressProvider>;
}
